import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { getServiceCases } from '@/controller/serviceCase';
// Import Tailwind components instead of Antd
import { CardSkeleton, Empty, Pagination, Skeleton, TableSkeleton, toast } from '@/shared/components/UI';
import ActionMenu from '@/shared/components/UI/ActionMenu';
import { LIST_EMPTY_PLACEHOLDER } from '@/shared/constants';

const SearchIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
    </svg>
);

const getActionByType = (actions, type) =>
    Array.isArray(actions)
        ? actions.find((action) => action.type === type && action.url && action.url.trim() !== '')
        : null;

const SignOffIcon = ({ title }) => (
    <svg title={title} fill="currentColor" viewBox="0 0 256 256" style={{ width: '16px', height: '16px' }}>
        <path d="M232,168H63.86c2.66-5.24,5.33-10.63,8-16.11,15,1.65,32.58-8.78,52.66-31.14,5,13.46,14.45,30.93,30.58,31.25,9.06.18,18.11-5.2,27.42-16.37C189.31,143.75,203.3,152,232,152a8,8,0,0,0,0-16c-30.43,0-39.43-10.45-40-16.11a7.67,7.67,0,0,0-5.46-7.75,8.14,8.14,0,0,0-9.25,3.49c-12.07,18.54-19.38,20.43-21.92,20.37-8.26-.16-16.66-19.52-19.54-33.42a8,8,0,0,0-14.09-3.37C101.54,124.55,88,133.08,79.57,135.29,88.06,116.42,94.4,99.85,98.46,85.9c6.82-23.44,7.32-39.83,1.51-50.1-3-5.38-9.34-11.8-22.06-11.8C61.85,24,49.18,39.18,43.14,65.65c-3.59,15.71-4.18,33.21-1.62,48s7.87,25.55,15.59,31.94c-3.73,7.72-7.53,15.26-11.23,22.41H24a8,8,0,0,0,0,16H37.41c-11.32,21-20.12,35.64-20.26,35.88a8,8,0,1,0,13.71,8.24c.15-.26,11.27-18.79,24.7-44.12H232a8,8,0,0,0,0-16ZM58.74,69.21C62.72,51.74,70.43,40,77.91,40c5.33,0,7.1,1.86,8.13,3.67,3,5.33,6.52,24.19-21.66,86.39C56.12,118.78,53.31,93,58.74,69.21Z"></path>
    </svg>
);

const SignOffIcon2 = ({ title }) => (
    <svg
        title={title}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: '16px', height: '16px' }}
    >
        <path d="M12 20h9" />
        <path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z" />
        <path d="m15 5 3 3" />
    </svg>
);

const DocumentIcon = ({ title }) => (
    <svg
        title={title}
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="2"
        stroke="currentColor"
        style={{ width: '16px', height: '16px' }}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
        />
    </svg>
);

const ExportPdfIcon = ({ className, onClick, title }) => (
    <svg
        className={className}
        onClick={onClick}
        title={title}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ width: '16px', height: '16px' }}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
        />
    </svg>
);

const limit = 20;

const ViewServiceCase = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [pageSetting, setPageSetting] = useState({});
    const [page, setPage] = useState(1);
    const [activeTab, setActiveTab] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Debounce search term to reduce API calls
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 1000); // 1000ms delay for debouncing

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const getServiceCaseList = async (type = '', filter = '') => {
            setLoading(true);
            try {
                // Pass type and filter parameters to API for server-side filtering
                const ret = await getServiceCases(type, page, limit, filter);
                if (ret.status === 200) {
                    setPageSetting(ret.data?.meta === undefined ? {} : ret.data?.meta);
                    setData(Array.isArray(ret.data?.data) ? ret.data?.data : []);
                    setLoading(false);
                } else if (ret.status !== 401) {
                    // Don't show toast for 401 errors - handled by global interceptor
                    toast.error(ret.data?.error?.message);
                }
            } catch (e) {
                toast.error('An error occurred while fetching service cases.');
            }
            setLoading(false);
        };

        getServiceCaseList(activeTab, debouncedSearchTerm);
    }, [page, activeTab, debouncedSearchTerm]);

    // Reset to page 1 when filters change
    useEffect(() => {
        if (page !== 1) {
            setPage(1);
        }
    }, [activeTab, debouncedSearchTerm]);

    const chgPage = (e) => {
        setPage(e);
    };

    const getMenuItems = (serviceCase) => {
        const deliveryOrderAction = getActionByType(serviceCase.actions, 'service-case-delivery-order');
        const serviceCaseSignOffAction = getActionByType(serviceCase.actions, 'service-case-sign-off');

        return [
            deliveryOrderAction && {
                label: deliveryOrderAction.action === 'sign-off' ? 'Sign Off Delivery Order' : 'Delivery Order Report',
                icon: deliveryOrderAction.action === 'sign-off' ? <SignOffIcon2 /> : <DocumentIcon />,
                onClick: () => {
                    if (deliveryOrderAction.url) {
                        window.open(deliveryOrderAction.url, '_blank', 'noopener');
                    }
                },
            },
            serviceCaseSignOffAction && {
                label: serviceCaseSignOffAction.action === 'sign-off' ? 'Sign Off Service Case' : 'Service Case Report',
                icon: serviceCaseSignOffAction.action === 'sign-off' ? <SignOffIcon /> : <DocumentIcon />,
                onClick: () => {
                    if (serviceCaseSignOffAction.url) {
                        window.open(serviceCaseSignOffAction.url, '_blank', 'noopener');
                    }
                },
            },
        ].filter(Boolean);
    };

    const renderMobileCard = (serviceCase) => (
        <div
            key={serviceCase.id}
            className="cursor-pointer bg-white border border-[#d4d9e3] rounded-xl p-4"
            onClick={() => navigate(`/member/view-service-case/${serviceCase.case_no}`)}
        >
            <div className="flex items-start gap-3">
                <div className="flex-1 space-y-3">
                    <div className="flex justify-between items-start">
                        <div className="flex-1">
                            <h3 className="text-[#101319] text-base font-medium leading-normal">
                                {serviceCase.case_no || LIST_EMPTY_PLACEHOLDER}
                            </h3>
                        </div>
                        {serviceCase.status_data && (
                            <button
                                className={`flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-8 px-4 text-white text-sm font-medium leading-normal`}
                                style={{ backgroundColor: serviceCase.status_data.color }}
                            >
                                <span className="truncate">{serviceCase.status_data.name}</span>
                            </button>
                        )}
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-[#5b6b8b] text-sm">Serial Number:</span>
                            <span className="text-[#101319] text-sm font-medium">
                                {serviceCase.serial_number || LIST_EMPTY_PLACEHOLDER}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-[#5b6b8b] text-sm">Product:</span>
                            <span className="text-[#101319] text-sm font-medium">
                                {serviceCase.product?.name || LIST_EMPTY_PLACEHOLDER}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-[#5b6b8b] text-sm">Subject:</span>
                            <span className="text-[#101319] text-sm font-medium">
                                {serviceCase.subject || LIST_EMPTY_PLACEHOLDER}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-[#5b6b8b] text-sm">Created Date:</span>
                            <span className="text-[#101319] text-sm font-medium">
                                {' '}
                                {serviceCase.created_at
                                    ? dayjs(serviceCase.created_at).format('YYYY-MM-DD')
                                    : LIST_EMPTY_PLACEHOLDER}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[#5b6b8b] text-sm">Actions:</span>
                            <div className="flex gap-2">
                                {/* <ExportPdfIcon
                                    className={`text-sm cursor-pointer text-gray-300 cursor-not-allowed'}`}
                                    // onClick={() => toast.error('Export to PDF is not implemented yet')}
                                    title="Export to PDF"
                                /> */}
                                <ActionMenu menuItems={getMenuItems(serviceCase)} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="relative flex size-full min-h-screen flex-col bg-white">
            <div className="layout-container flex h-full grow flex-col">
                <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5">
                    <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
                        {/* Header */}
                        <div className="flex flex-wrap justify-between gap-3 p-4">
                            <div className="flex min-w-72 flex-col gap-3">
                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight">
                                    Service Cases
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal">
                                    View your service cases
                                </p>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="px-4 py-3">
                            <label className="flex flex-col min-w-40 h-12 w-full">
                                <div className="flex w-full flex-1 items-stretch rounded-xl h-full">
                                    <div className="text-[#5b6b8b] flex border-none bg-[#e9ecf1] items-center justify-center pl-4 rounded-l-xl border-r-0">
                                        <SearchIcon />
                                    </div>
                                    <input
                                        placeholder="Search by product"
                                        className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-[#101319] focus:outline-0 focus:ring-0 border-none bg-[#e9ecf1] focus:border-none h-full placeholder:text-[#5b6b8b] px-4 rounded-l-none border-l-0 pl-2 text-base font-normal leading-normal"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </label>
                        </div>

                        {/* Content */}
                        <div className="px-4 py-3">
                            {loading ? (
                                <>
                                    <div className="hidden md:block">
                                        <TableSkeleton rows={5} columns={7} />
                                    </div>
                                    <div className="md:hidden space-y-4">
                                        {[...Array(3)].map((_, i) => (
                                            <CardSkeleton key={i} rows={4} />
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Desktop Table */}
                                    <div className="hidden md:flex overflow-hidden rounded-xl border border-[#d4d9e3] bg-gray-50">
                                        <table className="flex-1">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Service Case Number
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Serial Number
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Product
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Subject
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Created Date
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#101319] text-sm font-medium leading-normal">
                                                        Status
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[#5b6b8b] text-sm font-medium leading-normal">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.length > 0 ? (
                                                    data.map((serviceCase, index) => (
                                                        <tr
                                                            key={serviceCase.id || index}
                                                            className="border-t border-t-[#d4d9e3]"
                                                        >
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {serviceCase.case_no ? (
                                                                    <Link
                                                                        to={`/member/view-service-case/${serviceCase.case_no}`}
                                                                        className="text-[#2563eb] font-medium no-underline hover:underline"
                                                                    >
                                                                        {serviceCase.case_no}
                                                                    </Link>
                                                                ) : (
                                                                    LIST_EMPTY_PLACEHOLDER
                                                                )}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {serviceCase.serial_number || LIST_EMPTY_PLACEHOLDER}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {serviceCase.product?.name || LIST_EMPTY_PLACEHOLDER}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {serviceCase.subject || LIST_EMPTY_PLACEHOLDER}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {serviceCase.created_at
                                                                    ? dayjs(serviceCase.created_at).format('YYYY-MM-DD')
                                                                    : LIST_EMPTY_PLACEHOLDER}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-normal leading-normal">
                                                                {serviceCase.status_data ? (
                                                                    <button
                                                                        className={`flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-8 px-4 text-white text-sm font-medium leading-normal w-full`}
                                                                        style={{
                                                                            backgroundColor:
                                                                                serviceCase.status_data?.color ||
                                                                                '#d4d9e3',
                                                                        }}
                                                                    >
                                                                        <span className="truncate">
                                                                            {serviceCase.status_data?.name}
                                                                        </span>
                                                                    </button>
                                                                ) : (
                                                                    LIST_EMPTY_PLACEHOLDER
                                                                )}
                                                            </td>
                                                            <td className="h-[72px] px-4 py-2 text-[#5b6b8b] text-sm font-bold leading-normal tracking-[0.015em]">
                                                                <div className="flex gap-2">
                                                                    <ActionMenu menuItems={getMenuItems(serviceCase)} />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={7} className="px-4 py-12 text-center">
                                                            <Empty
                                                                image="simple"
                                                                description={
                                                                    <span className="text-[#5b6b8b] text-sm font-normal">
                                                                        {debouncedSearchTerm
                                                                            ? 'No service cases match your search'
                                                                            : `No ${activeTab} service cases found`}
                                                                    </span>
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Card Layout */}
                                    <div className="md:hidden space-y-4">
                                        {data.length > 0 ? (
                                            data.map(renderMobileCard)
                                        ) : (
                                            <div className="text-center py-12">
                                                <Empty
                                                    image="simple"
                                                    description={
                                                        <span className="text-[#5b6b8b] text-sm font-normal">
                                                            {debouncedSearchTerm
                                                                ? 'No service cases match your search'
                                                                : `No ${activeTab} service cases found`}
                                                        </span>
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {data.length > 0 && pageSetting.total > limit && (
                                        <div className="flex items-center justify-center mt-8">
                                            <Pagination
                                                current={page}
                                                pageSize={limit}
                                                total={pageSetting.total}
                                                onChange={setPage}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Bottom spacing */}
                        <div className="pb-24" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewServiceCase;
